import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AnimateAutoHeight from '@src/scripts/components/AnimateAutoHeight';

import { Splide, SplideSlide } from '@splidejs/react-splide';
import app from '@src/scripts/react-habitat/app';

// eslint-disable-next-line import/no-unresolved
import * as TabsBlockStyles from './custom.styled.UNIVERSITY';

const TabsBlock = ( {
	align,
	children,
	selected,
	mobileHorizontal,
	layout,
	l10n,
	isEditor = false,
	tabs = [],
} ) => {
	// if ( mobileHorizontal ) {
	// 	layout.orientation = 'horizontal';
	// }

	const isSpaceBetween = layout.justifyContent === 'space-between';
	const isVertical = layout.orientation === 'vertical';
	const isWrapAllowed = layout.flexWrap === 'wrap';

	const ref = useRef();
	const splideRef = useRef();

	useEffect( () => {
		if ( ref.current ) {
			app.update( ref.current );
		}
	}, [ ref ] );

	const navAtts = {
		isWrapAllowed,
		className: [
			'tabs-nav',
			isSpaceBetween ? 'stretch' : '',
			'align' + layout.justifyContent,
		]
			.filter( ( v ) => v !== '' )
			.join( ' ' ),
		style: ! isSpaceBetween
			? { justifyContent: layout.justifyContent }
			: {},
	};

	useEffect( () => {
		if ( ! ref.current || ! splideRef.current || isEditor ) {
			return;
		}

		// Callback function to execute when mutations are observed
		const callback = ( mutationList ) => {
			for ( const mutation of mutationList ) {
				if (
					mutation.type === 'attributes' &&
					mutation.attributeName === 'data-open'
				) {
					if ( mutation.target.dataset.open === 'true' ) {
						const slideIndex = parseInt(
							mutation.target.closest( '.tabItemContainer' )
								.dataset.tabIndex
						);

						splideRef.current.splide.go( slideIndex );
					}
				}
			}
		};

		// Create an observer instance linked to the callback function
		const observer = new MutationObserver( callback );

		// Start observing the target node for configured mutations
		observer.observe( ref.current, {
			attributes: true,
			childList: true,
			subtree: true,
		} );

		// Later, you can stop observing
		return () => observer.disconnect();
	}, [ ref, isEditor, splideRef ] );

	// If wrap is not allowed, we need to add a slider for horizontal scrolling of tabs

	return (
		<>
			{ mobileHorizontal && isVertical ? (
				<>
					<TabsBlockStyles.VerticalTabsWrapper
						className={ `dhsv-tabs align${ align } is-vertical mobile-horizontal` }
						data-selected={ selected }
						ref={ ref }
					>
						{ /* eslint-disable-next-line no-nested-ternary */ }
						{ isEditor ? (
							<TabsBlockStyles.Nav { ...navAtts }>
								{ children }
							</TabsBlockStyles.Nav>
						) : (
							<>
								<Splide
									options={ {
										arrows: false,
										drag: true,
										autoWidth: true,
										gap: '1rem',
										focus: 'left',
										start: 0,
									} }
									className="tabs-nav"
									ref={ splideRef }
								>
									{ tabs.map( ( tab, key ) => (
										<SplideSlide
											key={ key }
											style={ { whiteSpace: 'nowrap' } }
										>
											<div
												className="tabItemContainer"
												data-tab-index={ key }
												dangerouslySetInnerHTML={ {
													__html: tab,
												} }
											/>
										</SplideSlide>
									) ) }
								</Splide>
							</>
						) }

						<TabsBlockStyles.BodyWrapper>
							<AnimateAutoHeight>
								<TabsBlockStyles.Body
									className={ 'tabs-body' }
								/>
							</AnimateAutoHeight>
							<TabsBlockStyles.EditorEndLine>
								{ l10n?.endTabContent }
							</TabsBlockStyles.EditorEndLine>
						</TabsBlockStyles.BodyWrapper>
					</TabsBlockStyles.VerticalTabsWrapper>
				</>
			) : (
				React.createElement(
					isVertical
						? TabsBlockStyles.VerticalTabsWrapper
						: TabsBlockStyles.TabsWrapper,
					{
						className: `dhsv-tabs align${ align } ${
							isVertical ? 'is-vertical' : ''
						}`,
						'data-selected': selected,
						ref,
					},
					<>
						{ /* eslint-disable-next-line no-nested-ternary */ }
						{ isEditor ? (
							<TabsBlockStyles.Nav { ...navAtts }>
								{ children }
							</TabsBlockStyles.Nav>
						) : ! isVertical && ! isWrapAllowed ? (
							<Splide
								options={ {
									arrows: false,
									drag: true,
									autoWidth: true,
									gap: '1rem',
									focus: 'left',
									start: 0,
								} }
								className="tabs-nav"
								ref={ splideRef }
							>
								{ tabs.map( ( tab, key ) => (
									<SplideSlide
										key={ key }
										style={ { whiteSpace: 'nowrap' } }
									>
										<div
											className="tabItemContainer"
											data-tab-index={ key }
											dangerouslySetInnerHTML={ {
												__html: tab,
											} }
										/>
									</SplideSlide>
								) ) }
							</Splide>
						) : (
							<TabsBlockStyles.Nav { ...navAtts }>
								{ tabs.map( ( tab, key ) => (
									<div
										key={ key }
										className="tabItemContainer"
										data-tab-index={ key }
										dangerouslySetInnerHTML={ {
											__html: tab,
										} }
									/>
								) ) }
							</TabsBlockStyles.Nav>
						) }

						<TabsBlockStyles.BodyWrapper>
							<AnimateAutoHeight>
								<TabsBlockStyles.Body
									className={ 'tabs-body' }
								/>
							</AnimateAutoHeight>
							<TabsBlockStyles.EditorEndLine>
								{ l10n?.endTabContent }
							</TabsBlockStyles.EditorEndLine>
						</TabsBlockStyles.BodyWrapper>
					</>
				)
			) }
		</>
	);

	return React.createElement(
		isVertical
			? TabsBlockStyles.VerticalTabsWrapper
			: TabsBlockStyles.TabsWrapper,
		{
			className: `dhsv-tabs align${ align } ${
				isVertical ? 'is-vertical' : ''
			}`,
			'data-selected': selected,
			ref,
		},
		<>
			{ /* eslint-disable-next-line no-nested-ternary */ }
			{ isEditor ? (
				<TabsBlockStyles.Nav { ...navAtts }>
					{ children }
				</TabsBlockStyles.Nav>
			) : ! isVertical && ! isWrapAllowed ? (
				<Splide
					options={ {
						arrows: false,
						drag: true,
						autoWidth: true,
						gap: '1rem',
						focus: 'center',
						start: 0,
					} }
					className="tabs-nav"
					ref={ splideRef }
				>
					{ tabs.map( ( tab, key ) => (
						<SplideSlide
							key={ key }
							style={ { whiteSpace: 'nowrap' } }
						>
							<div
								className="tabItemContainer"
								data-tab-index={ key }
								dangerouslySetInnerHTML={ {
									__html: tab,
								} }
							/>
						</SplideSlide>
					) ) }
				</Splide>
			) : (
				<TabsBlockStyles.Nav { ...navAtts }>
					{ tabs.map( ( tab, key ) => (
						<div
							key={ key }
							className="tabItemContainer"
							data-tab-index={ key }
							dangerouslySetInnerHTML={ { __html: tab } }
						/>
					) ) }
				</TabsBlockStyles.Nav>
			) }

			<TabsBlockStyles.BodyWrapper>
				<AnimateAutoHeight>
					<TabsBlockStyles.Body className={ 'tabs-body' } />
				</AnimateAutoHeight>
				<TabsBlockStyles.EditorEndLine>
					{ l10n?.endTabContent }
				</TabsBlockStyles.EditorEndLine>
			</TabsBlockStyles.BodyWrapper>
		</>
	);
};

TabsBlock.propTypes = {
	align: PropTypes.string,
	children: PropTypes.node,
	selected: PropTypes.string,
	mobileHorizontal: PropTypes.bool,
	layout: PropTypes.shape( {
		justifyContent: PropTypes.string,
		flexWrap: PropTypes.string,
		orientation: PropTypes.string,
	} ),
	l10n: PropTypes.object,
	isEditor: PropTypes.bool,
	tabs: PropTypes.array,
};

export default TabsBlock;
